/**
 * Access state store for sponsor authentication and access tier.
 *
 * ACT 5: Deferred-payment access gate implementation.
 * Access state is explicit and separate from real payment.
 *
 * Security note: Access state must be validated server-side.
 * This store is for UI state only, not authorization decisions.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logout as apiLogout } from '@/api/auth';
import { replaceLocation } from '@/utils/browserRedirect';

/**
 * Explicit sponsor access states for ACT 5 deferred-payment gate.
 * These states represent the access tier regardless of payment status.
 */
export type SponsorAccessState =
  | 'unknown'           // Initial state, not yet determined
  | 'no_access'          // Authenticated but no sponsor access granted
  | 'manual_access'     // Access granted manually/admin/dev-side
  | 'paid_placeholder'; // Placeholder for when payments are implemented

/**
 * Explicit auth bootstrap status.
 * Used to handle loading states and avoid stale UI on transient errors.
 *
 * Transitions:
 * - 'loading': Bootstrap in progress
 * - 'authenticated': Server validated session exists
 * - 'anonymous': Server validated no session
 * - 'unknown': Bootstrap failed (transient error) - preserve last known state
 */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'unknown';

/**
 * Registration kind from backend session.
 * Product intent: determines what CTAs are appropriate for the account.
 *
 * - 'sponsor': Account registered as a sponsor, may see sponsor-related UI
 * - 'friend': Account registered as a friend, must NOT see sponsor CTAs
 * - 'unclassified': New public-signup users before onboarding, valid authenticated state
 */
export type RegistrationKind = 'sponsor' | 'friend' | 'unclassified' | '';

export interface AuthState {
  // Auth bootstrap status (explicit states for UX safety)
  authStatus: AuthStatus;

  // Session state (derived from authStatus + sessionId)
  isAuthenticated: boolean;
  sessionId: string | null;

  // Account ID from session (used for profile attribution)
  // SECURITY: accountId is operational data, never rendered or logged
  accountId: string | null;

  // Registration kind (product intent) from backend session
  // Used to gate sponsor-related CTAs appropriately
  registrationKind: RegistrationKind;

  // Sponsor access state (ACT 5: explicit deferred-payment gate)
  // Access state is separate from real payment implementation
  sponsorAccessState: SponsorAccessState;

  // Legacy fields for backward compatibility during transition
  hasSponsorAccess: boolean;
  sponsorTier: 'none' | 'pending' | 'active';

  // Admin role state
  // TODO: Connect to real admin role from backend (see admin/backend role epic)
  // Currently uses demo predicate for development only
  isAdmin: boolean;

  // Fingerprint context (for audit continuity)
  // TODO: Bind to session, validate server-side
  fingerprintId: string | null;

  // Actions
  setSession: (sessionId: string | null) => void;
  setSponsorAccess: (hasAccess: boolean, tier?: AuthState['sponsorTier']) => void;
  setAccessState: (state: SponsorAccessState) => void;
  setAdmin: (isAdmin: boolean) => void;
  setFingerprint: (fingerprintId: string | null) => void;
  setAuthStatus: (status: AuthStatus) => void;
  bootstrapStart: () => void;
  bootstrapSuccess: (sessionId: string) => void;
  bootstrapFailure: () => void;
  /**
   * Hydrate auth state from backend session response.
   * This is the canonical way to sync frontend state with backend after login or app load.
   *
   * SECURITY: Sponsor access and admin role are derived from backend response only.
   * No client-provided role claims are trusted.
   */
  hydrateFromSession: (session: {
    session_id: string;
    account_id: string;
    registration_kind: RegistrationKind;
    has_sponsor_access: boolean;
    sponsor_tier: 'none' | 'sponsor';
    is_admin: boolean;
  }) => void;
  /**
   * Logout current session.
   * - Calls backend /logout endpoint to invalidate server-side session
   * - Clears all frontend auth state
   * - Redirects to /login
   * - Fail-safe: always clears local state even if backend fails
   */
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      authStatus: 'unknown',
      isAuthenticated: false,
      sessionId: null,
      accountId: null,
      sponsorAccessState: 'unknown',
      hasSponsorAccess: false,
      sponsorTier: 'none',
      isAdmin: false,
      fingerprintId: null,

      setSession: (sessionId) =>
        set({ sessionId, isAuthenticated: !!sessionId }),

      setSponsorAccess: (hasAccess, tier = 'none') =>
        set({ hasSponsorAccess: hasAccess, sponsorTier: tier }),

      setAccessState: (state) => {
        // Sync legacy fields with new access state
        const isGranted = state === 'manual_access' || state === 'paid_placeholder';
        set({
          sponsorAccessState: state,
          hasSponsorAccess: isGranted,
          sponsorTier: isGranted ? 'active' : 'none',
        });
      },

      setAdmin: (isAdmin) => set({ isAdmin }),

      setFingerprint: (fingerprintId) =>
        set({ fingerprintId }),

      setAuthStatus: (status) => set({ authStatus: status }),

      bootstrapStart: () => set({ authStatus: 'loading' }),

      bootstrapSuccess: (sessionId) => set({
        authStatus: 'authenticated',
        sessionId,
        isAuthenticated: true,
      }),

      bootstrapFailure: () => {
        // On bootstrap failure (transient error), preserve current state
        // Set authStatus to 'unknown' to indicate validation failed
        // but don't clear session - let user retry
        set({ authStatus: 'unknown' });
      },

      hydrateFromSession: (session) => {
        // Derive sponsor access state from backend response
        const sponsorAccessState =
          session.has_sponsor_access ? 'manual_access'
          : session.registration_kind === 'sponsor' ? 'no_access'
          : 'unknown';

        set({
          authStatus: 'authenticated',
          sessionId: session.session_id,
          accountId: session.account_id,
          isAuthenticated: true,
          registrationKind: session.registration_kind,
          sponsorAccessState,
          hasSponsorAccess: session.has_sponsor_access,
          sponsorTier: session.sponsor_tier === 'none' ? 'none' : 'active',
          isAdmin: session.is_admin,
          // fingerprintId intentionally not set here - comes from fingerprinting flow
        });
      },

      logout: async () => {
        // Call backend logout endpoint to invalidate server-side session.
        // The server-side session MUST be invalidated for security.
        let serverLogoutSuccess = false;
        try {
          await apiLogout();
          serverLogoutSuccess = true;
        } catch (error) {
          console.error('Backend logout failed:', error);
          // NOTE: Session may still be valid server-side - this is a security concern
          // that requires production smoke testing to verify the backend fix works.
        }

        // Clear all frontend auth state
        set({
          authStatus: 'anonymous',
          isAuthenticated: false,
          sessionId: null,
          accountId: null,
          registrationKind: '',
          sponsorAccessState: 'unknown',
          hasSponsorAccess: false,
          sponsorTier: 'none',
          isAdmin: false,
          fingerprintId: null,
        });

        // Redirect to root `/` using replace() to prevent back-button session resurrection.
        // NOTE: replace() only affects browser history; it cannot fix a still-valid server cookie.
        // The backend fix ensures logout succeeds server-side, which is the primary defense.
        //
        // ACT-INDEEP-ROOT-MOBILE-LOGIN01: Root is now the mobile login screen,
        // so redirecting to `/` shows the login UI after logout.
        replaceLocation('/');
      },
    }),
    // CORRECTION03: persist options are produced by a single factory
    // so the integration test in
    // `frontend/src/__tests__/persist_wiring/` can re-import the EXACT
    // options wired into the production store and exercise them
    // against the REAL `zustand/middleware`. The factory is defined
    // below this store.
    createAuthPersistOptions()
  )
);

/**
 * Production persist options for the auth store.
 *
 * Exported as a factory (CORRECTION03 of
 * ACT-INDEEP-PRODUCTION-ACCOUNT-AUTH-HYDRATION-RECOVERY01) so the
 * exact persist configuration wired into the production store can be
 * re-imported and exercised against the REAL `zustand/middleware` in
 * a dedicated integration test (see
 * `frontend/src/__tests__/persist_wiring/indeep-auth.persist.wiring.test.ts`).
 *
 * If you remove `merge: mergePersistedAuthState` from the factory,
 * the wiring witness in `persist_wiring/indeep-auth.persist.wiring.test.ts`
 * will fail with semantic assertions about leaked hostile authority
 * (CORRECTION04 P0-01 SEMANTIC_WIRING_RED). The other options are
 * structural and are asserted by the cheap `createAuthPersistOptions
 * exposes expected structural keys` test, but only `merge:` has a
 * semantic-RED witness — removing the others would be caught by
 * the structural test alone, not by the production-shaped hostile-
 * legacy scenarios.
 *
 * The order of fields below matches the production store wiring
 * verbatim. The factory itself is the only producer of these options;
 * the production store is the only consumer.
 *
 * @see ACT-INDEEP-PRODUCTION-ACCOUNT-AUTH-HYDRATION-RECOVERY01-CORRECTION03
 */
export function createAuthPersistOptions() {
  return {
    name: 'indeep-auth',
    // Do not persist sensitive data (see partializeAuthState below).
    partialize: partializeAuthState,
    // CORRECTION02 of ACT-INDEEP-PRODUCTION-ACCOUNT-AUTH-HYDRATION-RECOVERY01:
    // ATOMIC trust boundary at persistence. The merge function runs BEFORE
    // Zustand publishes hydrated state via set(...). Stale authentication
    // authority from localStorage NEVER enters live store state at all —
    // mergePersistedAuthState drops it deterministically. This is the only
    // place where persisted material becomes live store state.
    //
    // The previous (CORRECTION01) implementation used onRehydrateStorage,
    // which runs AFTER Zustand's default-merge has already installed
    // persisted state. With synchronous localStorage that window is small
    // (React has not mounted yet), but the trust boundary was still not
    // atomic — a future async storage backend or post-mount rehydrate()
    // call could publish stale auth before the post-callback projection
    // ran. Moving the projection into merge closes that window.
    merge: mergePersistedAuthState,
    // onRehydrateStorage is intentionally NOT the auth trust boundary.
    // It is retained as a no-op hook for future telemetry/hydration
    // bookkeeping (e.g. emitting an analytics event on successful
    // rehydration) without any authority mutation.
    onRehydrateStorage: () => (state: AuthState | undefined) => {
      if (!state) return;
      // No-op by design. Authority projection happens in merge.
    },
  } as const;
}

/**
 * Auth state fields that are safe to persist to localStorage.
 *
 * SECURITY (CORRECTION02 of ACT-INDEEP-PRODUCTION-ACCOUNT-AUTH-HYDRATION-RECOVERY01):
 * Only NON-AUTHORITATIVE UX hints are persisted. Authentication authority
 * (authStatus, isAuthenticated, sessionId, isAdmin, fingerprintId) is NEVER
 * restored from localStorage because localStorage is:
 *   1. Client-controlled (tampering, stale data, multi-tab races)
 *   2. Synchronously hydrated at store creation — BEFORE /api/auth/me can
 *      validate the session (per React useEffect timing).
 *   3. The wrong trust boundary for route authorization.
 *
 * Persisted fields and their authority model:
 * - registrationKind     : UI hint only (nav label). Re-validated by /me.
 * - sponsorAccessState   : UI hint (e.g. paywall copy). Re-validated by /me.
 * - sponsorTier          : UI hint (legacy display). Re-validated by /me.
 *
 * NOT persisted (authoritative, must come from server each session):
 * - authStatus           : route guard source of truth
 * - isAuthenticated      : derived companion of authStatus
 * - sessionId            : server session identifier
 * - accountId            : operational, never rendered or logged
 * - isAdmin              : role authority
 * - fingerprintId        : audit continuity
 * - hasSponsorAccess     : derived from sponsorAccessState
 *
 * E2E auth injection path (per ACT-INDEEP-FRONTEND-FEED-COMPOSER01-CORRECTION02):
 * E2E tests do NOT rely on persisted authStatus. They seed
 * `window.__INDEEP_E2E_AUTH_STATE__` via Playwright `page.addInitScript()`
 * BEFORE main.tsx runs `initializeE2EAuthInjection()`, which calls
 * `setState({...})` to project the session synchronously. Production
 * never enters this path (gated by `import.meta.env.MODE !== 'e2e'`).
 *
 * @see ADR-001 audit architecture for session validation policy
 * @see ACT-INDEEP-PRODUCTION-ACCOUNT-AUTH-HYDRATION-RECOVERY01-CORRECTION02
 */
export function partializeAuthState(state: AuthState) {
  return {
    // registrationKind is persisted for nav label continuity
    // It is NOT the source of truth - server /me response governs auth.
    // registrationKind cannot authorize access because auth guards use
    // fresh server-derived authentication state; rehydration resets
    // isAuthenticated=false/authStatus='unknown' until /api/auth/me resolves.
    registrationKind: state.registrationKind,
    // sponsorAccessState is persisted for paywall/UI copy continuity.
    // Re-validated by /me on every bootstrap.
    sponsorAccessState: state.sponsorAccessState,
    // sponsorTier is a legacy display field. Re-validated by /me.
    sponsorTier: state.sponsorTier,
    // NOTE: fingerprintId is intentionally not persisted
    // to maintain anonymous posture across sessions.
  } as Pick<AuthState, 'registrationKind' | 'sponsorAccessState' | 'sponsorTier'>;
}

/**
 * ATOMIC persistence trust boundary (CORRECTION02).
 *
 * This function is wired as `persist.merge` in the store configuration
 * above. It is the SOLE point at which persisted material becomes live
 * store state — Zustand's default-merge does NOT run.
 *
 * SECURITY: stale authentication authority from localStorage NEVER enters
 * live store state at all. Even if a previous build of the app wrote
 * `authStatus='authenticated'` (or any other authority field) to
 * localStorage, this function deterministically projects authority back
 * to the canonical cold-start defaults before set(...) publishes state.
 *
 * Authority fields (always reset to fresh cold-start defaults):
 *   authStatus='unknown'
 *   isAuthenticated=false
 *   sessionId=null
 *   accountId=null
 *   isAdmin=false
 *   hasSponsorAccess=false
 *   fingerprintId=null
 *
 * UX-hint fields (allowed to survive rehydration):
 *   registrationKind
 *   sponsorAccessState
 *   sponsorTier
 *
 * The signature matches Zustand's persist.merge contract:
 *   merge: (persistedState: unknown, currentState: S) => S
 *
 * Exported for direct unit testing — tests call this exact function
 * (not a handwritten copy) to prove the production authority projection.
 *
 * @see ACT-INDEEP-PRODUCTION-ACCOUNT-AUTH-HYDRATION-RECOVERY01-CORRECTION02
 */
export function mergePersistedAuthState(
  persisted: unknown,
  current: AuthState,
): AuthState {
  // The `persisted` argument is the output of partializeAuthState from a
  // prior session. It MAY contain only the allowlisted UX-hint fields
  // (registrationKind, sponsorAccessState, sponsorTier) if produced by a
  // post-CORRECTION01 build. It MAY contain stale authority fields if
  // produced by a pre-CORRECTION01 build. Either way, the merge
  // deterministically projects authority to fresh defaults.
  const hints = (persisted ?? {}) as Partial<AuthState>;

  return {
    ...current,
    // UX hints survive (non-authoritative; re-validated by /me).
    //
    // PERSISTED_HINT_RUNTIME_VALIDATION = DEFERRED (per CORRECTION03 P1-03).
    // Zustand's JSON persistence layer (`createJSONStorage`) does not
    // validate runtime shape — it JSON-parses whatever was written and
    // hands it to merge. We deliberately do NOT add enum/type guards
    // here because these three fields are non-authoritative: they are
    // re-validated by /api/auth/me on every bootstrap, and the route
    // authorization predicates read `isAuthenticated` (which we reset
    // here), not these hint fields. So even if a hostile payload
    // smuggles garbage into `registrationKind` / `sponsorAccessState`
    // / `sponsorTier`, the authority projection below is unchanged
    // and route guards remain correct. Adding validation here would
    // not strengthen the trust boundary; it would just normalise a
    // field that downstream /me is about to overwrite anyway. See
    // `persist_wiring/indeep-auth.persist.wiring.test.ts` →
    // `garbage UX-hint values do not pollute authority projection`
    // for the negative witness.
    registrationKind: hints.registrationKind ?? current.registrationKind,
    sponsorAccessState: hints.sponsorAccessState ?? current.sponsorAccessState,
    sponsorTier: hints.sponsorTier ?? current.sponsorTier,
    // Authentication authority is ALWAYS fresh — never restored from
    // localStorage. These are the canonical cold-start defaults; the
    // bootstrap (/api/auth/me) will transition them to authenticated or
    // anonymous after first paint.
    authStatus: 'unknown',
    isAuthenticated: false,
    sessionId: null,
    accountId: null,
    isAdmin: false,
    hasSponsorAccess: false,
    fingerprintId: null,
  };
}

/**
 * Hook to check if user has active sponsor access.
 * Access gate for /discover, /card/:id, /intro-request.
 *
 * ACT 5: Uses explicit sponsorAccessState for deferred-payment gate.
 * Access is granted when state is 'manual_access' or 'paid_placeholder'.
 */
export const useHasSponsorAccess = () => {
  const { sponsorAccessState, hasSponsorAccess, sponsorTier } = useAuthStore();
  // Primary check uses explicit access state
  const hasExplicitAccess = sponsorAccessState === 'manual_access' || sponsorAccessState === 'paid_placeholder';
  // Fallback to legacy fields for compatibility
  return hasExplicitAccess || (hasSponsorAccess && sponsorTier === 'active');
};

/**
 * Hook to check if user has admin role.
 * Admin guard for /admin and other admin-only routes.
 *
 * TODO: Replace demo predicate with real admin role check when
 * admin/backend role epic is implemented.
 *
 * Demo predicate for development:
 * Admin is granted when sessionId contains 'admin' (for demo purposes only).
 * This is NOT secure - only for local development and demos.
 */
export const useIsAdmin = () => {
  const { isAdmin, sessionId } = useAuthStore();
  // TODO: Replace this demo predicate with real admin role check
  // from backend API when admin/backend role epic is implemented
  const isDemoAdmin = sessionId?.includes('admin') ?? false;
  return isAdmin || isDemoAdmin;
};

/**
 * Determines if the sponsor access CTA card should be shown.
 *
 * Key invariant: registration_kind is product intent; sponsor access is entitlement.
 * The UI must not collapse both into "not sponsored yet".
 *
 * Conditions for showing the CTA:
 * - registration_kind must be 'sponsor' (friend accounts must NOT see sponsor CTAs)
 * - hasSponsorAccess must be false (already granted access = show curated path)
 * - isAuthenticated must be true (session exists)
 *
 * @param state - The auth state (from store or for testing)
 * @returns true if the sponsor access CTA should be shown
 */
export function shouldShowSponsorAccessCard(state: {
  isAuthenticated: boolean;
  registrationKind: RegistrationKind;
  hasSponsorAccess: boolean;
  sponsorAccessState: SponsorAccessState;
}): boolean {
  return (
    state.isAuthenticated &&
    state.registrationKind === 'sponsor' &&
    !state.hasSponsorAccess &&
    state.sponsorAccessState !== 'manual_access' &&
    state.sponsorAccessState !== 'paid_placeholder'
  );
}

/**
 * Hook to determine if sponsor access CTA card should be shown.
 * Uses current auth store state.
 */
export const useShouldShowSponsorAccessCard = () => {
  const { isAuthenticated, registrationKind, hasSponsorAccess, sponsorAccessState } = useAuthStore();
  return shouldShowSponsorAccessCard({ isAuthenticated, registrationKind, hasSponsorAccess, sponsorAccessState });
};

/**
 * Authenticated home experience types.
 * Represents the distinct experiences for different authenticated user states.
 */
export type HomeExperience =
  | 'anonymous'           // Not authenticated
  | 'unclassified'        // New public-signup users before onboarding completion
  | 'friend'              // Friend account - first-class dashboard
  | 'sponsor_needs_access' // Sponsor without access - show access CTA
  | 'sponsor_active'       // Sponsor with access - full dashboard
  | 'unknown';            // Malformed session - safe fallback

/**
 * Determines which home experience to show based on auth state.
 * This is the canonical way to determine which dashboard to render.
 *
 * Priority:
 * 1. Not authenticated => anonymous
 * 2. registrationKind === 'unclassified' => unclassified (needs onboarding)
 * 3. registrationKind === 'friend' => friend (first-class, no sponsor CTAs)
 * 4. registrationKind === 'sponsor' && !hasSponsorAccess => sponsor_needs_access
 * 5. registrationKind === 'sponsor' && hasSponsorAccess => sponsor_active
 * 6. Other => unknown (safe fallback)
 */
export function homeExperienceForAuthState(state: {
  isAuthenticated: boolean;
  registrationKind: RegistrationKind;
  hasSponsorAccess: boolean;
  sponsorAccessState: SponsorAccessState;
}): HomeExperience {
  if (!state.isAuthenticated) {
    return 'anonymous';
  }

  // Unclassified users need to complete onboarding to become friend or sponsor
  if (state.registrationKind === 'unclassified') {
    return 'unclassified';
  }

  if (state.registrationKind === 'friend') {
    return 'friend';
  }

  if (state.registrationKind === 'sponsor') {
    if (state.hasSponsorAccess ||
        state.sponsorAccessState === 'manual_access' ||
        state.sponsorAccessState === 'paid_placeholder') {
      return 'sponsor_active';
    }
    return 'sponsor_needs_access';
  }

  // Malformed session (authenticated but no valid registrationKind)
  return 'unknown';
}

/**
 * Hook to get the current home experience type.
 * Use this to determine which dashboard component to render.
 */
export const useHomeExperience = (): HomeExperience => {
  const { isAuthenticated, registrationKind, hasSponsorAccess, sponsorAccessState } = useAuthStore();
  return homeExperienceForAuthState({ isAuthenticated, registrationKind, hasSponsorAccess, sponsorAccessState });
};
